import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useState } from "react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { CartDrawer } from "./CartDrawer";
import { AnnouncementBar } from "./AnnouncementBar";
import { useLanguage, isRTL } from "@/lib/i18n";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import logo from "@/assets/logo.png";
export function Header() {
  const {
    t,
    language
  } = useLanguage();
  const rtl = isRTL(language);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleSectionClick = useCallback((e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      navigate('/');
      setTimeout(() => scrollToSection(sectionId), 150);
    }
  }, [location.pathname, navigate, scrollToSection]);

  const handleLogoClick = useCallback((e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  }, [location.pathname]);

  
  return <>
    <AnnouncementBar />
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container relative flex h-16 items-center justify-between">
        {/* Mobile: Hamburger on start edge */}
        <div className="md:hidden flex items-center">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button className="p-2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side={rtl ? "right" : "left"} className="w-72 bg-background border-border p-0">
              <nav className="flex flex-col pt-12 px-6" aria-label="Mobile navigation">
                <Link to="/" onClick={handleLogoClick} className="py-3 text-base font-medium text-foreground hover:text-primary transition-colors border-b border-border/50">
                  {t('nav.home')}
                </Link>
                <a href="/#products" onClick={(e) => handleSectionClick(e, 'products')} className="py-3 text-base font-medium text-foreground hover:text-primary transition-colors border-b border-border/50 cursor-pointer">
                  {t('nav.shop')}
                </a>
                <a href="/#about" onClick={(e) => handleSectionClick(e, 'about')} className="py-3 text-base font-medium text-foreground hover:text-primary transition-colors border-b border-border/50 cursor-pointer">
                  {t('nav.about')}
                </a>
                <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="py-3 text-base font-medium text-foreground hover:text-primary transition-colors border-b border-border/50">
                  {t('nav.blog')}
                </Link>

                {/* Mobile switchers */}
                <div className="flex items-center gap-4 mt-6 pt-4 border-t border-border/50">
                  <CurrencySwitcher />
                  <LanguageSwitcher />
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        {/* Logo - centered absolutely */}
        <Link to="/" onClick={handleLogoClick} className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3 group">
          <img src={logo} alt="PDRN SKIN logo" className="w-14 h-14 object-contain" />
          <div className="flex items-baseline gap-1.5" dir="ltr">
            <span className="text-2xl font-bold tracking-wider bg-gradient-to-r from-primary via-pink-400 to-primary bg-clip-text text-transparent group-hover:opacity-80 transition-opacity">
              PDRN
            </span>
            <span className="text-[0.65rem] font-medium tracking-widest uppercase text-muted-foreground">
              SKIN
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
          <Link to="/" onClick={handleLogoClick} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            {t('nav.home')}
          </Link>
          <a href="/#products" onClick={(e) => handleSectionClick(e, 'products')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
            {t('nav.shop')}
          </a>
          <a href="/#about" onClick={(e) => handleSectionClick(e, 'about')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
            {t('nav.about')}
          </a>
          <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            {t('nav.blog')}
          </Link>
        </nav>

        {/* Actions - end edge */}
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-3">
            <CurrencySwitcher />
            <LanguageSwitcher />
          </div>
          <div className={rtl ? "md:me-0 -me-1" : ""}>
            <CartDrawer />
          </div>
        </div>
      </div>
    </header>
    </>;
}